import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { FileText, Landmark, Loader2, Upload } from "lucide-react";
import { AdminReo } from "@/components/realtor/AdminReo";

const W9 = ({ d, setD }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { setD((await api.post("/admin/rt/w9", fd)).data); toast.success("W-9 uploaded"); } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  const view = async () => {
    try { const r = await api.get("/admin/rt/w9", { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Card className="mb-5 p-6" data-testid="admin-rt-w9-card">
      <h3 className="font-display text-lg font-bold text-slate-900">W-9 form</h3>
      <p className="mb-3 text-xs text-slate-500">One W-9 Ava attaches to every realtor's REO applications when a website asks for it. Realtors never see it.</p>
      <div className="flex flex-wrap items-center gap-3">
        {d.w9 ? <button type="button" onClick={view} className="flex items-center gap-1 text-sm text-blue-700 underline" data-testid="admin-rt-w9-view"><FileText className="h-4 w-4" />{d.w9.filename}</button>
          : <span className="text-sm text-slate-500" data-testid="admin-rt-w9-none">Not uploaded</span>}
        <Button variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="admin-rt-w9-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1 h-4 w-4" />{d.w9 ? "Replace" : "Upload"}</>}</Button>
        <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} data-testid="admin-rt-w9-file" />
      </div>
    </Card>
  );
};

const Bank = ({ d, setD }) => {
  const b = d.bank;
  const [f, setF] = useState({ bank_name: b.bank_name, account_name: b.account_name, account_type: b.account_type || "checking", routing: "", account: "" });
  const save = async () => {
    try { setD((await api.put("/admin/rt/bank", f)).data); setF((x) => ({ ...x, routing: "", account: "" })); toast.success("Banking info saved"); } catch (err) { toast.error(apiError(err)); }
  };
  const field = (k, l, ph = "") => <div><Label>{l}</Label><Input className="mt-1" value={f[k]} placeholder={ph} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`admin-rt-bank-${k}`} /></div>;
  return (
    <Card className="mb-5 p-6" data-testid="admin-rt-bank-card">
      <h3 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Landmark className="h-5 w-5" />Banking info</h3>
      <p className="mb-3 text-xs text-slate-500">One account Ava enters on every realtor's REO applications only where a website asks where to deposit commissions. Never used to pay fees; realtors never see it.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("bank_name", "Bank name")}{field("account_name", "Account holder name")}
        {field("routing", "Routing number", b.routing_masked ? `Saved ${b.routing_masked} - leave blank to keep` : "9 digits")}
        {field("account", "Account number", b.account_masked ? `Saved ${b.account_masked} - leave blank to keep` : "")}
        <div><Label>Account type</Label>
          <select className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={f.account_type} onChange={(e) => setF({ ...f, account_type: e.target.value })} data-testid="admin-rt-bank-account_type">
            <option value="checking">Checking</option><option value="savings">Savings</option></select></div>
      </div>
      <Button className="mt-4" onClick={save} data-testid="admin-rt-bank-save">Save banking info</Button>
      {b.updated_at && <span className="ml-3 text-xs text-slate-500" data-testid="admin-rt-bank-saved">Saved {new Date(b.updated_at).toLocaleDateString()}</span>}
    </Card>
  );
};

export default function AdminRealtors() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/rt/shared").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  return (
    <div className="mx-auto max-w-5xl" data-testid="admin-realtors-page">
      <h1 className="font-display text-3xl font-extrabold text-slate-900">Realtors</h1>
      <p className="mb-5 text-sm text-slate-600">Shared documents and banking info for REO applications, plus each realtor's progress and website fees.</p>
      {d && <><W9 d={d} setD={setD} /><Bank d={d} setD={setD} /></>}
      <AdminReo />
    </div>
  );
}
